/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { cn } from '@bem-react/classname'

import { Memo } from '@/hoc/Memo'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'

import useBackendError from '@/hooks/useBackendError'

import { Back } from '@/components/Back'

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from 'recharts'

import './styles/StatisticPage.scss'
import { getCompanystatisticThunk } from '../LicensesPage/store/licenses.slice'
import { DateFilterModel } from '@/core/interfaces/data-filter'
import { Filter } from '@/components/Filter'
import { companystatisticSelector } from '../LicensesPage/store/licenses.selector'
import { Title } from '@/components/Title'
import { Checkbox } from '@/components/form/Checkbox'

import totalDTM8 from './icons/total-dtm8.svg'
import totalDTM7 from './icons/total-dtm7.svg'
import expiredDTM8 from './icons/expired-dtm8.svg'
import expiredDTM7 from './icons/expired-dtm7.svg'
import demoDTM8 from './icons/demo-dtm8.svg'
import demoDTM7 from './icons/demo-dtm7.svg'
import keenanDTM8 from './icons/keenan-dtm8.svg'
import keenanDTM7 from './icons/keenan-dtm7.svg'
import icDTM8 from './icons/ic-dtm8.svg'
import icDTM7 from './icons/ic-dtm7.svg'
import proadvDTM8 from './icons/proadv-dtm8.svg'
import proDTM7 from './icons/pro-dtm7.svg'
import advDTM7 from './icons/adv-dtm7.svg'
import useWindowSize from '@/hooks/useWindowSize'
import { CustomTooltip } from './CustomTooltip'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'

const cnStatistic = cn('Statistic')

export const StatisticPage: FC = Memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()
  const [selectedLines, setSelectedLines] = useState<string[]>(['dtM8', 'dtM7'])
  const { width } = useWindowSize()

  const first = [
    {
      value: 'dtM8',
      label: 'DTM 8',
      dataKey: 'dtM8',
      stroke: '#99BC00',
      type: 'linear',
      dashed: '0',
    },
    {
      value: 'dtM7',
      label: 'DTM 7',
      dataKey: 'dtM7',
      stroke: '#99BC00',
      type: 'monotone',
      dashed: '5 5',
    },
    {
      value: 'expiredDTM8',
      label: 'DTM 8',
      dataKey: 'expiredDTM8',
      stroke: '#C61D42',
      type: 'monotone',
      dashed: '0',
    },
    {
      value: 'expiredDTM7',
      label: 'DTM 7',
      dataKey: 'expiredDTM7',
      stroke: '#C61D42',
      type: 'monotone',
      dashed: '5 5',
    },
    {
      value: 'demoDTM8',
      label: 'DTM 8',
      dataKey: 'demoDTM8',
      stroke: '#E8B500',
      type: 'monotone',
      dashed: '0',
    },
    {
      value: 'demoDTM7',
      label: 'DTM 7',
      dataKey: 'demoDTM7',
      stroke: '#E8B500',
      type: 'monotone',
      dashed: '5 5',
    },
  ]

  const second = [
    {
      value: 'alltechDTM8',
      label: 'DTM 8',
      dataKey: 'alltechDTM8',
      stroke: '#1B51A8',
      name: 'Total DTM 2',
      type: 'monotone',
    },
    {
      value: 'alltechDTM7',
      label: 'DTM 7',
      dataKey: 'alltechDTM7',
      stroke: '#1B51A8',
      name: 'Total DTM 1',
      type: 'monotone',
      dashed: '5 5',
    },
    {
      value: 'icdtM8',
      label: 'DTM 8',
      dataKey: 'icdtM8',
      stroke: '#169882',
      type: 'monotone',
    },
    {
      value: 'icdtM7',
      label: 'DTM 7',
      dataKey: 'icdtM7',
      stroke: '#169882',
      type: 'monotone',
      dashed: '5 5',
    },
    {
      value: 'advProDTM8',
      label: 'DTM8 PRO/ADV',
      dataKey: 'advProDTM8',
      stroke: '#6D38B7',
      type: 'monotone',
    },
    {
      value: 'proDTM7',
      label: 'DTM7 PRO',
      dataKey: 'proDTM7',
      stroke: '#6D38B7',
      type: 'monotone',
      dashed: '5 5',
    },
    {
      value: 'advDTM7',
      label: 'DTM7 ADV',
      dataKey: 'advDTM7',
      stroke: '#AF3FA0',
      type: 'monotone',
      dashed: '5 5',
    },
  ]

  const linesData = [
    {
      value: 'dtM8',
      label: 'DTM 8',
      dataKey: 'dtM8',
      stroke: '#99BC00',
      type: 'linear',
      dashed: '0',
      icon: totalDTM8,
    },
    {
      value: 'dtM7',
      label: 'DTM 7',
      dataKey: 'dtM7',
      stroke: '#99BC00',
      type: 'monotone',
      dashed: '5 5',
      icon: totalDTM7,
    },
    {
      value: 'expiredDTM8',
      label: 'DTM 8',
      dataKey: 'expiredDTM8',
      stroke: '#C61D42',
      type: 'monotone',
      dashed: '0',
      icon: expiredDTM8,
    },
    {
      value: 'expiredDTM7',
      label: 'DTM 7',
      dataKey: 'expiredDTM7',
      stroke: '#C61D42',
      type: 'monotone',
      dashed: '5 5',
      icon: expiredDTM7,
    },
    {
      value: 'demoDTM8',
      label: 'DTM 8',
      dataKey: 'demoDTM8',
      stroke: '#E8B500',
      type: 'monotone',
      dashed: '0',
      icon: demoDTM8,
    },
    {
      value: 'demoDTM7',
      label: 'DTM 7',
      dataKey: 'demoDTM7',
      stroke: '#E8B500',
      type: 'monotone',
      dashed: '5 5',
      icon: demoDTM7,
    },
    {
      value: 'alltechDTM8',
      label: 'DTM 8',
      dataKey: 'alltechDTM8',
      stroke: '#1B51A8',
      name: 'Total DTM 2',
      type: 'monotone',
      icon: keenanDTM8,
    },
    {
      value: 'alltechDTM7',
      label: 'DTM 7',
      dataKey: 'alltechDTM7',
      stroke: '#1B51A8',
      name: 'Total DTM 1',
      type: 'monotone',
      dashed: '5 5',
      icon: keenanDTM7,
    },
    {
      value: 'icdtM8',
      label: 'DTM 8',
      dataKey: 'icdtM8',
      stroke: '#169882',
      type: 'monotone',
      icon: icDTM8,
    },
    {
      value: 'icdtM7',
      label: 'DTM 7',
      dataKey: 'icdtM7',
      stroke: '#169882',
      type: 'monotone',
      dashed: '5 5',
      icon: icDTM7,
    },
    {
      value: 'advProDTM8',
      label: 'DTM8 PRO/ADV',
      dataKey: 'advProDTM8',
      stroke: '#6D38B7',
      type: 'monotone',
      icon: proadvDTM8,
    },
    {
      value: 'proDTM7',
      label: 'DTM7 PRO',
      dataKey: 'proDTM7',
      stroke: '#6D38B7',
      type: 'monotone',
      dashed: '5 5',
      icon: proDTM7,
    },
    {
      value: 'advDTM7',
      label: 'DTM7 ADV',
      dataKey: 'advDTM7',
      stroke: '#AF3FA0',
      type: 'monotone',
      dashed: '5 5',
      icon: advDTM7,
    },
  ]

  const [date, setDate] = useState<DateFilterModel>({} as DateFilterModel)

  const companystatistic = useAppSelector(companystatisticSelector)

  const loadData = useCallback(async () => {
    dispatch(changeLoading(true))
    try {
      await dispatch(getCompanystatisticThunk(date)).unwrap()
      dispatch(changeLoading(false))
    } catch (errors) {
      setBackendError(errors)
      dispatch(changeLoading(false))
    }
  }, [date])

  useEffect(() => {
    if (date.startDate && date.endDate) {
      loadData()
    }
  }, [date])

  useEffect(() => {
    dispatch(changeLoading(true))
  }, [])

  const dataToCSV = (data: any) => {
    if (!data || data.length === 0) return ''
    const header = Object.keys(data[0]).join(',')
    const rows = data.map((row: any) => Object.values(row).join(','))
    return `${header}\n${rows.join('\n')}`
  }

  const handleDownloadCSV = () => {
    const csvData = dataToCSV(companystatistic)
    if (!csvData) {
      dispatch(
        setToast({
          type: ToastType.error,
          message: t('No data available for download'),
        }),
      )
      return
    }
    const csvBlob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
    const csvURL = URL.createObjectURL(csvBlob)

    const downloadLink = document.createElement('a')
    downloadLink.href = csvURL
    downloadLink.download = 'companies_statistic.csv'
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(csvURL)
  }

  const handleCheckboxChange = (event: any) => {
    const value = event.target.value
    setSelectedLines(prevSelectedLines => {
      if (prevSelectedLines.includes(value)) {
        // Если линия уже выбрана, убираем ее из состояния
        return prevSelectedLines.filter(line => line !== value)
      } else {
        // Если линия не выбрана, добавляем ее в состояние
        return [...prevSelectedLines, value]
      }
    })
  }

  const handleShowAllChangeFirst = (event: any) => {
    if (event.target.checked) {
      // Выбрать все линии
      setSelectedLines(first.map(line => line.value))
    } else {
      // Снять выбор со всех линий
      setSelectedLines([])
    }
  }

  const handleShowAllChangeSecond = (event: any) => {
    if (event.target.checked) {
      // Выбрать все линии
      setSelectedLines(second.map(line => line.value))
    } else {
      // Снять выбор со всех линий
      setSelectedLines([])
    }
  }

  const checkAllUnchecked = () => {
    if (selectedLines.length === 0) {
      setSelectedLines(['dtM8', 'dtM7'])
    }
  }

  // Вызов проверки при изменении состояния selectedLines
  useEffect(() => {
    checkAllUnchecked()
  }, [selectedLines])

  const formatDate = (dateString: any) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${day}/${month}/${year}`
  }

  const renderLines = () => {
    return selectedLines.map(lineValue => {
      const lineConfig = linesData.find(line => line.value === lineValue)
      if (lineConfig) {
        return (
          <Line
            key={lineConfig.value}
            dataKey={lineConfig.dataKey}
            stroke={lineConfig.stroke}
            strokeDasharray={lineConfig.dashed}
            strokeWidth={2}
          />
        )
      }
      return null
    })
  }

  const sortedData = [...companystatistic].sort((a, b) => {
    const dateA = new Date(a.date).valueOf()
    const dateB = new Date(b.date).valueOf()
    return dateA - dateB
  })

  const hideBrushTick = () => {
    return ''
  }

  return (
    <>
      <Helmet>
        <title>{t('Account & Billing Information')} | Registration</title>
      </Helmet>
      <div className={cnStatistic()}>
        <Back />
        <div className={cnStatistic('item')}>
          <Title className={cnStatistic('title')}>
            {t('Companies trend (statistic)')}
          </Title>
          <div className={cnStatistic('wrap')}>
            <div className={cnStatistic('top')}>
              <Filter changeDate={date => setDate(date)} />
              <button
                onClick={handleDownloadCSV}
                className={cnStatistic('download')}
              >
                <i
                  className={cnStatistic('icon', ['fa-solid fa-download'])}
                ></i>
                {t('Download CSV')}
              </button>
            </div>
            <div className={cnStatistic('bottom')}>
              <div className={cnStatistic('container')}>
                <div className={cnStatistic('name')}>Total</div>
                {linesData.slice(0, 2).map(line => (
                  <div key={line.value}>
                    <Checkbox
                      type='checkbox'
                      value={line.value}
                      label={line.label}
                      checked={selectedLines.includes(line.value)}
                      onChange={handleCheckboxChange}
                      className={cnStatistic('checkbox')}
                    />

                    <img
                      src={line.icon}
                      alt=''
                      className={cnStatistic('img')}
                    />
                  </div>
                ))}
              </div>

              <div className={cnStatistic('container')}>
                <div className={cnStatistic('name')}>Expired</div>
                {linesData.slice(2, 4).map(line => (
                  <div key={line.value}>
                    <Checkbox
                      type='checkbox'
                      value={line.value}
                      label={line.label}
                      checked={selectedLines.includes(line.value)}
                      onChange={handleCheckboxChange}
                      className={cnStatistic('checkbox')}
                    />
                    <img
                      src={line.icon}
                      alt=''
                      className={cnStatistic('img')}
                    />
                  </div>
                ))}
              </div>

              <div className={cnStatistic('container')}>
                <div className={cnStatistic('name')}>Demo</div>
                {linesData.slice(4, 6).map(line => (
                  <div key={line.value}>
                    <Checkbox
                      type='checkbox'
                      value={line.value}
                      label={line.label}
                      checked={selectedLines.includes(line.value)}
                      onChange={handleCheckboxChange}
                      className={cnStatistic('checkbox')}
                    />
                    <img
                      src={line.icon}
                      alt=''
                      className={cnStatistic('img')}
                    />
                  </div>
                ))}
              </div>

              <div className={cnStatistic('border')}>
                <div>
                  <Checkbox
                    type='checkbox'
                    onChange={handleShowAllChangeFirst}
                    label={t('Show all')}
                    checked={selectedLines.length === first.length}
                  />
                </div>
              </div>

              {width <= 991 && <div className={cnStatistic('empty')}></div>}

              <div className={cnStatistic('container')}>
                <div className={cnStatistic('name')}>Keenan</div>
                {linesData.slice(6, 8).map(line => (
                  <div key={line.value}>
                    <Checkbox
                      type='checkbox'
                      value={line.value}
                      label={line.label}
                      checked={selectedLines.includes(line.value)}
                      onChange={handleCheckboxChange}
                      className={cnStatistic('checkbox')}
                    />
                    <img
                      src={line.icon}
                      alt=''
                      className={cnStatistic('img')}
                    />
                  </div>
                ))}
              </div>

              <div className={cnStatistic('container')}>
                <div className={cnStatistic('name')}>IC</div>
                {linesData.slice(8, 10).map(line => (
                  <div key={line.value}>
                    <Checkbox
                      type='checkbox'
                      value={line.value}
                      label={line.label}
                      checked={selectedLines.includes(line.value)}
                      onChange={handleCheckboxChange}
                      className={cnStatistic('checkbox')}
                    />
                    <img
                      src={line.icon}
                      alt=''
                      className={cnStatistic('img')}
                    />
                  </div>
                ))}
              </div>

              <div className={cnStatistic('container')}>
                <div className={cnStatistic('name')}>PRO/ADV</div>
                {linesData.slice(10, 13).map(line => (
                  <div key={line.value}>
                    <Checkbox
                      type='checkbox'
                      value={line.value}
                      label={line.label}
                      checked={selectedLines.includes(line.value)}
                      onChange={handleCheckboxChange}
                      className={cnStatistic('checkbox')}
                    />
                    <img
                      src={line.icon}
                      alt=''
                      className={cnStatistic('img')}
                    />
                  </div>
                ))}
              </div>

              <div className={cnStatistic('all')}>
                <div>
                  <Checkbox
                    type='checkbox'
                    onChange={handleShowAllChangeSecond}
                    label={t('Show all')}
                    checked={selectedLines.length === second.length}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {companystatistic.length !== 0 ? (
          <ResponsiveContainer width='100%' height={350}>
            <LineChart
              data={sortedData}
              margin={{
                top: 5,
                right: 0,
                left: 0,
                bottom: 5,
              }}
              height={500}
            >
              <XAxis
                dataKey='date'
                tickFormatter={formatDate}
                interval='preserveStartEnd'
                tick={{
                  fill: '#002c53',
                  fontWeight: '400',
                  fontSize: '11px',
                }}
                tickSize={10}
              />
              <YAxis
                tick={{
                  fill: '#002c53',
                  fontWeight: '400',
                  fontSize: '12px',
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Brush
                height={10}
                endIndex={10}
                tickFormatter={hideBrushTick}
                stroke='#002c53'
                style={{ backgroundColor: '#000' }}
              />
              <Legend />
              {renderLines()}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className={cnStatistic('nodata')}>{t('No data')}</div>
        )}
      </div>
    </>
  )
})
